import React from 'react';
import ImageIcon from '@material-ui/icons/Image';
import ClearIcon from '@material-ui/icons/Replay';
import Button from '@material-ui/core/Button';
import LinearProgress from '@material-ui/core/LinearProgress';
import axios from 'axios';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Icon from '@mdi/react';
import {mdiGoogleDrive, mdiImage, mdiUpload} from '@mdi/js';

import {getBackgroundImageUrl} from 'client/utils/slides';
import GooglePicker from 'client/components/common/ui/GooglePicker';
import css from './BaseFileUpload.pcss';
import appConsts from 'client/constants/appConsts';
import bytesToSizeString from 'client/utils/bytesToSizeString';
import ElementVectorCollectionModal from 'client/components/editor/modals/ElementVectorCollectionModal';
import getFileUploader from 'client/utils/getFileUploader';
import {SlingshotDirectiveName} from 'shared/collections/Attachments';
import {LL} from 'shared/localization';

interface IEditorImageProps {
	type: UploaderFileType;
	value?: string;
	title?: string;
	onChange: (fileLink: string) => void;
	height?: number;
}

interface IEditorImageState {
	uploadProgress: number;
	uploading: boolean;
	anchorEl?: HTMLElement;
	openCollection: boolean;
}

interface IFileUploadProgress {
	total: number;
	loaded: number;
}

export enum UploaderFileType {
	Image = 'img',
	Any = 'any',
}

class BaseFileUpload extends React.PureComponent<IEditorImageProps, IEditorImageState> {
	static defaultProps: Partial<IEditorImageProps> = {
		type: UploaderFileType.Any,
	};

	state: IEditorImageState = {uploadProgress: 0, uploading: false, openCollection: false};

	handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files?.length) {
			return;
		}

		const files = Array.from(e.target.files);

		if (files.some((file) => file.size > appConsts.maxImgSize)) {
			alert(
				`${LL('common.ui.uploadVideoError')} (${bytesToSizeString(appConsts.maxImgSize)})`,
			);
			return;
		}

		this.setState({uploading: true, uploadProgress: 0});

		const uploader = getFileUploader(SlingshotDirectiveName.Images);
		uploader
			.send(files[0], (progress) => this.setState({uploadProgress: progress * 100}))
			.then(({s3Link}) => {
				this.setState({uploading: false});
				this.props.onChange(s3Link);
			})
			.catch((error) => {
				console.log('Upload Image Error: ', error);
				alert(LL('common.ui.uploadImageError'));
			});
	};

	handleClear = (e: React.MouseEvent) => {
		e.preventDefault();

		this.props.onChange('');
	};

	handleGooglePickerChange = (docs: any) => {
		this.props.onChange(`https://drive.google.com/uc?id=${docs[0].id}`);
	};

	handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		this.setState({anchorEl: event.currentTarget});
	};

	handleClose = () => {
		this.setState({anchorEl: undefined});
	};

	openCollectionModal = () => {
		this.setState({openCollection: true});
	};

	closeCollectionModal = () => {
		this.setState({openCollection: false});

		this.handleClose();
	};

	handleChoseElement = (path) => {
		this.props.onChange(path);
		this.closeCollectionModal();
	};

	render() {
		const {value, title, type, height} = this.props;
		const {uploadProgress, uploading, anchorEl, openCollection} = this.state;

		return (
			<label>
				<div
					className={css.zone}
					style={{
						height,
						backgroundImage: uploading ? '' : getBackgroundImageUrl(value),
					}}
				>
					{title && !value && <div className={css.title}>{title}</div>}

					{!uploading && (
						<>
							{value && (
								<div className={css.clear} onClick={this.handleClear}>
									<ClearIcon />
								</div>
							)}

							{!value && (
								<div className={css.icon}>
									<ImageIcon />
								</div>
							)}

							{/*<GooglePicker
								scope={['https://www.googleapis.com/auth/drive.readonly']}
								onChange={this.handleGooglePickerChange}
								navHidden={true}
								authImmediate={false}
								mimeTypes={['image/png', 'image/jpeg', 'image/jpg']}
								viewId={'DOCS'}
							>
								<Button component="span" className={css.button}>
									{!value ? 'Выбрать' : 'Изменить'}
								</Button>
							</GooglePicker>*/}

							<Button
								component="span"
								className={css.button}
								onClick={this.handleClick}
							>
								{!value ? LL('common.ui.choose') : LL('common.ui.change')}
							</Button>

							<Menu
								id="simple-menu"
								anchorEl={anchorEl}
								keepMounted
								open={Boolean(anchorEl)}
								onClose={this.handleClose}
							>
								<label>
									<MenuItem onClick={this.handleClose}>
										<ListItemIcon>
											<Icon size={1} path={mdiUpload} />
										</ListItemIcon>
										<ListItemText primary={LL('common.ui.onComputer')} />
									</MenuItem>

									<input
										className={css.input}
										accept={type === UploaderFileType.Image ? 'image/*' : '*'}
										onChange={this.handleFileChange}
										type="file"
									/>
								</label>

								<label>
									<MenuItem onClick={this.openCollectionModal}>
										<ListItemIcon>
											<Icon size={1} path={mdiImage} />
										</ListItemIcon>
										<ListItemText primary={LL('common.ui.fromCollection')} />
									</MenuItem>
								</label>

								<GooglePicker
									scope={['https://www.googleapis.com/auth/drive.readonly']}
									onChange={this.handleGooglePickerChange}
									navHidden={true}
									authImmediate={false}
									mimeTypes={['image/png', 'image/jpeg', 'image/jpg']}
									viewId={'DOCS'}
								>
									<MenuItem onClick={this.handleClose}>
										<ListItemIcon>
											<Icon size={1} path={mdiGoogleDrive} />
										</ListItemIcon>
										<ListItemText primary="Google" />
									</MenuItem>
								</GooglePicker>
							</Menu>
						</>
					)}

					{uploading && (
						<>
							<div className={css.loading}>
								{LL('common.loading')} {uploadProgress.toFixed(0)}%
							</div>

							<LinearProgress
								className={css.progress}
								variant="determinate"
								value={uploadProgress}
							/>
						</>
					)}
				</div>

				{/*<input
					className={css.input}
					accept={type === UploaderFileType.Image ? 'image/*' : '*'}
					onChange={this.handleFileChange}
					type="file"
				/>*/}
				<ElementVectorCollectionModal
					isOpen={openCollection || false}
					onClose={this.closeCollectionModal}
					onSubmit={this.handleChoseElement}
				/>
			</label>
		);
	}
}

export default BaseFileUpload;
